import { useCallback, useEffect, useState } from 'react';

import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';

import { useLazySearchSchedulesQuery } from '@/app/services/lookups';
import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { ScheduleMaster } from '@/rtk-query.gql';

import './ScheduleAutoComplete.css';

type ScheduleAutoCompleteProps = AutoCompleteProps & {
  className?: string;
  label?: string;
  defaultValue?: ScheduleMaster;
  forceNewSuggestionsRef?: boolean;
};

export const ScheduleAutoComplete = ({
  className,
  label,
  defaultValue,
  forceNewSuggestionsRef = false,
  ...autoCompleteProps
}: ScheduleAutoCompleteProps) => {
  const [selected, setSelected] = useState<ScheduleMaster | undefined>(
    defaultValue,
  );
  const [filtered, setFiltered] = useState<ScheduleMaster[]>([]);
  const [inputValue, setInputValue] = useState<
    ScheduleMaster | string | undefined
  >(defaultValue);

  const [query] = useLazySearchSchedulesQuery();

  useEffect(() => {
    setSelected(defaultValue);
    setInputValue(defaultValue);
  }, [defaultValue]);

  const search = useCallback(
    async (event: { query: string }) => {
      const { data = [] } = await query({
        search: event.query || '',
        selectionLimit: SELECTION_LIMIT,
      });

      setFiltered(forceNewSuggestionsRef ? [...data] : data);
    },
    [forceNewSuggestionsRef, query],
  );

  const handleSelect = (e: { value: ScheduleMaster }) => {
    setSelected(e.value);
    setInputValue(e.value);
  };

  const handleChange = (e: { value: string }) => {
    setInputValue(e.value);
  };

  return (
    <div
      className={`[ schedule-auto-complete ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      {label && <label>{label}</label>}
      <AutoComplete
        appendTo="self"
        suggestions={filtered}
        minLength={MIN_SEARCH_LENGTH}
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
      />
    </div>
  );
};
